import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { invalidate } from '@/lib/query';

import { useEntityApprovalActions } from '@/modules/approvals/hooks/useEntityApprovalActions';

import { ApprovalTimeline } from './ApprovalTimeline';

import { ApprovalStatusBadge } from './ApprovalStatusBadge';

import { ApprovalActionPanel } from './ApprovalActionPanel';

import { ReturnedApprovalBanner } from './ReturnedApprovalBanner';

import { ReturnedLeaveEditPanel } from './ReturnedLeaveEditPanel';

import { ReturnedAttendanceEditPanel } from './ReturnedAttendanceEditPanel';

import { ReturnedExpenseEditPanel } from './ReturnedExpenseEditPanel';

import { ReturnedTravelEditPanel } from './ReturnedTravelEditPanel';

import { LeaveApprovalContextDetails } from '@/modules/hr/components/LeaveApprovalContextDetails';
import { AttendanceApprovalContextDetails } from '@/modules/hr/components/AttendanceApprovalContextDetails';
import { OvertimeApprovalContextDetails } from '@/modules/hr/components/OvertimeApprovalContextDetails';

import { Button } from '@/components/ui/Button';

import {
  isLeaveApprovalReturned,
  isLeaveSubjectEmployee,
  supportsReturnedEntityEdit,
} from '@/modules/approvals/utils/returnedApprovalUtils';
import { useCurrentEmployeeId } from '@/modules/hr/hooks/useCurrentEmployeeId';



interface Props {

  ownerModule: string;

  entityType: string;

  entityId: number | null | undefined;

  className?: string;

  onActionComplete?: (action?: string) => void;

  /** Use ESS-scoped read APIs (employee viewing own leave). */

  essScope?: boolean;

  /** Allow the original requester to view/act without approval inbox permissions. */

  allowRequesterView?: boolean;

  /** Open the returned-leave edit form immediately. */

  autoEditReturned?: boolean;

  /** Leave record status — used when approval instance status has not synced yet. */

  leaveStatusCode?: string;

  /** Entity record status (leave statusCode or attendance approvalStatus). */

  recordStatusCode?: string;

  /** Subject employees.id — used to detect the record owner for edit & resubmit. */

  subjectEmployeeId?: number;

  /** Hide approve/reject/return controls (e.g. when shown inline in the list row). */

  showActions?: boolean;

  /** When false, skip leave reason/attachment block (e.g. already shown on parent card). */

  showLeaveContextDetails?: boolean;

  /** When false, skip attendance summary/breakdown (e.g. already shown on calendar card). */

  showAttendanceContextDetails?: boolean;

  /** When false, skip overtime summary with check-in/out (e.g. already shown on calendar card). */

  showOvertimeContextDetails?: boolean;

}



const OPEN_STATUSES = new Set(['PENDING', 'ESCALATED']);

const FINAL_STATUSES = new Set(['APPROVED', 'REJECTED', 'CANCELLED']);



export function EntityApprovalPanel({

  ownerModule,

  entityType,

  entityId,

  className,

  onActionComplete,

  essScope = false,

  allowRequesterView = false,

  autoEditReturned = false,

  leaveStatusCode,

  recordStatusCode,

  subjectEmployeeId,

  showActions = true,

  showLeaveContextDetails = true,

  showAttendanceContextDetails = true,

  showOvertimeContextDetails = true,

}: Props) {
  const qc = useQueryClient();
  const currentEmployeeId = useCurrentEmployeeId();
  const [editingReturned, setEditingReturned] = useState(autoEditReturned);



  useEffect(() => {

    if (autoEditReturned) {

      setEditingReturned(true);

    }

  }, [autoEditReturned, entityId]);



  const {

    instance,

    workflowUi,

    comments,

    setComments,

    acting,

    act,

    isFetched,

    isError,

    canShow,

    currentUserId,

    isRequester,

    refetch,

  } = useEntityApprovalActions({

    ownerModule,

    entityType,

    entityId,

    essScope,

    allowRequesterView,

    subjectEmployeeId,

    recordStatusCode,

    leaveStatusCode,

    onActionComplete: (action) => {
      if (action === 'RESUBMIT') {
        setEditingReturned(false);
      }
      onActionComplete?.(action);
    },

  });



  const timeline = instance?.timeline ?? [];



  if (!isFetched || isError || !instance || !canShow) {

    return null;

  }



  const isOpen = OPEN_STATUSES.has(instance.approvalStatus);

  const resolvedRecordStatus = recordStatusCode ?? leaveStatusCode;

  const isReturned = isLeaveApprovalReturned(instance, resolvedRecordStatus);

  const isFinal = FINAL_STATUSES.has(instance.approvalStatus);

  const isSubjectEmployee = isLeaveSubjectEmployee({
    currentEmployeeId,
    instance,
    subjectEmployeeId,
  });

  const editEssScope = essScope || (entityType === 'LEAVE' && isSubjectEmployee);

  const canEditReturned =

    isReturned &&

    isSubjectEmployee &&

    (workflowUi?.canEditReturned === true || supportsReturnedEntityEdit(entityType));

  const supportsInlineEdit = supportsReturnedEntityEdit(entityType);

  const workflowBound = workflowUi?.instanceId != null && workflowUi.instanceId > 0;

  const rowCanAct =

    instance.canAct === true &&

    (instance.approvalStatus === 'PENDING' || instance.approvalStatus === 'ESCALATED');

  const canAct = rowCanAct
    && (workflowBound
      ? !!(workflowUi?.canApprove || workflowUi?.canReject || workflowUi?.canReturn)
      : true);

  const pendingApprovers =

    instance.nextApproverSummary?.filter(Boolean).join(', ') ||

    workflowUi?.pendingWith ||

    undefined;

  const showRequesterActions = isRequester && (isReturned || isOpen);

  const showApprovalPanel = showActions && (canAct || showRequesterActions);

  const sectionTitle = isFinal || instance.approvalStatus === 'RETURNED' ? 'Approval history' : 'Approval workflow';



  return (

    <div className={className ?? 'mt-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700'}>

      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">

        <span className="font-medium text-slate-700 dark:text-slate-300">{sectionTitle}</span>

        <ApprovalStatusBadge status={instance.approvalStatus} />

      </div>



      {isReturned && isRequester ? (

        <div className="mb-3 space-y-3">

          <ReturnedApprovalBanner

            timeline={timeline}

            showEdit={false}

          />

          {canEditReturned && supportsInlineEdit && !editingReturned ? (

            <Button size="sm" variant="secondary" onClick={() => setEditingReturned(true)}>

              Edit & resubmit

            </Button>

          ) : null}

          {canEditReturned && entityType === 'LEAVE' && editingReturned && entityId != null ? (

            <ReturnedLeaveEditPanel

              leaveId={entityId}

              essScope={editEssScope}

              approvalReturned={isReturned}

              onSaved={() => {

                setEditingReturned(false);

                void refetch();

                invalidate.approvals(qc);

              }}

              onCancel={() => setEditingReturned(false)}

            />

          ) : null}

          {canEditReturned && entityType === 'ATTENDANCE' && editingReturned && entityId != null ? (

            <ReturnedAttendanceEditPanel

              attendanceId={entityId}

              approvalReturned={isReturned}

              onSaved={() => {

                setEditingReturned(false);

                void refetch();

                invalidate.approvals(qc);

              }}

              onCancel={() => setEditingReturned(false)}

            />

          ) : null}

          {canEditReturned && entityType === 'EXPENSE' && editingReturned && entityId != null ? (

            <ReturnedExpenseEditPanel

              claimId={entityId}

              onSaved={() => {

                setEditingReturned(false);

                void refetch();

                invalidate.approvals(qc);

              }}

              onCancel={() => setEditingReturned(false)}

            />

          ) : null}

          {canEditReturned && entityType === 'TRAVEL' && editingReturned && entityId != null ? (

            <ReturnedTravelEditPanel

              travelId={entityId}

              onSaved={() => {

                setEditingReturned(false);

                void refetch();

                invalidate.approvals(qc);

              }}

              onCancel={() => setEditingReturned(false)}

            />

          ) : null}

          {isReturned && isRequester ? (

            <p className="text-xs text-slate-500">

              Update the fields above, click <strong>Save changes</strong>, then <strong>Resubmit for approval</strong>.

            </p>

          ) : null}

        </div>

      ) : null}



      {isReturned && !isRequester ? (

        <p className="mb-3 text-xs text-slate-500">

          This request was returned to the submitter. Only the employee who submitted it can edit and resubmit.

        </p>

      ) : null}



      {isOpen && pendingApprovers ? (

        <div className="mb-2">

          <p className="text-xs text-slate-500">

            Pending with: {pendingApprovers}

          </p>

        </div>

      ) : null}



      {showActions && canAct ? (

        <p className="mb-2 text-xs text-slate-500">

          You can approve, reject, or return this request using the actions below.

        </p>

      ) : null}



      {showApprovalPanel ? (

        <ApprovalActionPanel

          row={instance}

          workflow={workflowUi}

          comments={comments}

          acting={acting}

          currentUserId={currentUserId}

          isRequesterOverride={supportsReturnedEntityEdit(entityType) ? isRequester : undefined}

          onCommentsChange={setComments}

          onAct={(action, delegateToUserId) => void act(action, delegateToUserId)}

        />

      ) : null}



      {(entityType === 'LEAVE' || entityType === 'LEAVE_CANCEL') && entityId != null && showLeaveContextDetails ? (
        <LeaveApprovalContextDetails leaveId={entityId} essScope={editEssScope} className="mt-3 mb-3" />
      ) : null}

      {(entityType === 'ATTENDANCE' || entityType === 'ATTENDANCE_CANCEL')
        && entityId != null
        && showAttendanceContextDetails ? (
        <AttendanceApprovalContextDetails attendanceId={entityId} className="mt-3 mb-3" />
      ) : null}

      {(entityType === 'OVERTIME' || entityType === 'OVERTIME_CANCEL')
        && entityId != null
        && showOvertimeContextDetails ? (
        <OvertimeApprovalContextDetails overtimeId={entityId} className="mt-3 mb-3" />
      ) : null}



      <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-700">

        <h5 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Timeline</h5>

        <ApprovalTimeline actions={timeline} />

      </div>

    </div>

  );

}


